/**
 * EmployeePortalForm — fiche employé côté client (Batch #14.251).
 */
import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders, Field, inputCls, isDemoResponse, formActionsCls, cancelBtnCls, saveBtnCls, FREQ_LABELS, PAY_TYPES, PAYMENT_METHODS, num, err } from "./_shared";

const EMPTY = {
  first_name: "", last_name: "", job_title: "", sin: "", birth_date: "", address: "", phone: "", email: "", hire_date: "", termination_date: "",
  status: "active", pay_frequency: "biweekly", pay_type: "hourly", hourly_rate: "", annual_salary: "", hours_per_period: 80,
  payment_method: "deposit", bank_institution: "", bank_transit: "", bank_account: "",
  ccq: { active: false, ccq_no: "", trade_id: "", level_code: "", union_name: "" }, notes: "",
};

function fromEmployee(e, defaultFreq) {
  if (!e) return { ...EMPTY, pay_frequency: defaultFreq || "biweekly" };
  const f = { ...EMPTY };
  Object.keys(EMPTY).forEach((k) => { if (e[k] !== undefined && e[k] !== null && k !== "ccq") f[k] = e[k]; });
  return { ...f, sin: "", bank_account: "", ccq: { ...EMPTY.ccq, ...(e.ccq || {}) } };
}

function Group({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 border-b pb-1">{title}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

export default function EmployeePortalForm({ initial, summary, primaryColor, onDone, onCancel }) {
  const [f, setF] = useState(() => fromEmployee(initial, summary.default_frequency));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setC = (k, v) => setF((x) => ({ ...x, ccq: { ...x.ccq, [k]: v } }));
  const trade = (summary.ccq_trades || []).find((t) => t.id === f.ccq.trade_id);

  const submit = async () => {
    if (!f.first_name.trim() || !f.last_name.trim()) { toast.error("Prénom et nom requis"); return; }
    if (f.pay_type === "hourly" && !(num(f.hourly_rate) > 0)) { toast.error("Taux horaire requis"); return; }
    if (f.pay_type === "salary" && !(num(f.annual_salary) > 0)) { toast.error("Salaire annuel requis"); return; }
    if (f.ccq.active && !f.ccq.trade_id) { toast.error("Choisissez le métier CCQ"); return; }
    setSaving(true);
    try {
      const body = { ...f, hourly_rate: num(f.hourly_rate), annual_salary: num(f.annual_salary), hours_per_period: num(f.hours_per_period),
        sin: f.sin.trim() || null, birth_date: f.birth_date || null, hire_date: f.hire_date || null, termination_date: f.termination_date || null,
        ccq: summary.ccq_enabled ? f.ccq : {} };
      const res = initial
        ? await axios.put(`${API}/portal/paie/employees/${initial.id}`, body, { headers: portalHeaders() })
        : await axios.post(`${API}/portal/paie/employees`, body, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success(initial ? "Fiche mise à jour — votre gestionnaire est avisé" : "Employé ajouté — votre gestionnaire complétera la fiche");
      onDone();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <div className="border-2 rounded-2xl p-4 space-y-4 bg-gray-50/60" style={{ borderColor: primaryColor }} data-testid="paie-portal-emp-form">
      <Group title="Identité">
        <Field label="Prénom *"><input className={inputCls} value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="paie-portal-emp-first" /></Field>
        <Field label="Nom *"><input className={inputCls} value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="paie-portal-emp-last" /></Field>
        <Field label={initial?.sin_masked ? `NAS (actuel ${initial.sin_masked})` : "NAS (9 chiffres)"}>
          <input className={inputCls} inputMode="numeric" value={f.sin} onChange={(e) => set("sin", e.target.value)} placeholder={initial?.sin_masked ? "Laisser vide pour conserver" : "123 456 789"} data-testid="paie-portal-emp-sin" />
        </Field>
        <Field label="Date de naissance"><input type="date" className={inputCls} value={f.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="paie-portal-emp-birth" /></Field>
        <Field label="Téléphone"><input className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Courriel (réception du bulletin)"><input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="paie-portal-emp-email" /></Field>
        <Field label="Adresse complète" className="sm:col-span-2 lg:col-span-3"><input className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="N°, rue, ville (Québec) code postal" /></Field>
      </Group>

      <Group title="Emploi et rémunération">
        <Field label="Poste"><input className={inputCls} value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="paie-portal-emp-job" /></Field>
        <Field label="Date d'embauche"><input type="date" className={inputCls} value={f.hire_date} onChange={(e) => set("hire_date", e.target.value)} /></Field>
        <Field label="Statut">
          <select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="paie-portal-emp-status">
            <option value="active">Actif</option><option value="inactive">Inactif (départ)</option>
          </select>
        </Field>
        {f.status === "inactive" && <Field label="Date de fin d'emploi"><input type="date" className={inputCls} value={f.termination_date} onChange={(e) => set("termination_date", e.target.value)} /></Field>}
        <Field label="Fréquence de paie">
          <select className={inputCls} value={f.pay_frequency} onChange={(e) => set("pay_frequency", e.target.value)} data-testid="paie-portal-emp-frequency">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Type de rémunération">
          <select className={inputCls} value={f.pay_type} onChange={(e) => set("pay_type", e.target.value)} data-testid="paie-portal-emp-pay-type">
            {Object.entries(PAY_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        {f.pay_type === "hourly"
          ? <Field label="Taux horaire ($) *"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} data-testid="paie-portal-emp-rate" /></Field>
          : <Field label="Salaire annuel ($) *"><input type="number" inputMode="decimal" step="0.01" min="0" className={inputCls} value={f.annual_salary} onChange={(e) => set("annual_salary", e.target.value)} data-testid="paie-portal-emp-salary" /></Field>}
        <Field label="Heures habituelles par période"><input type="number" inputMode="decimal" step="0.25" min="0" className={inputCls} value={f.hours_per_period} onChange={(e) => set("hours_per_period", e.target.value)} data-testid="paie-portal-emp-hours" /></Field>
      </Group>

      <Group title="Mode de paiement">
        <Field label="Méthode">
          <select className={inputCls} value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="paie-portal-emp-payment">
            {Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        {f.payment_method === "deposit" && (
          <>
            <Field label="Institution (3 chiffres)"><input className={inputCls} inputMode="numeric" value={f.bank_institution} onChange={(e) => set("bank_institution", e.target.value)} placeholder="815" data-testid="paie-portal-emp-bank-inst" /></Field>
            <Field label="Transit (5 chiffres)"><input className={inputCls} inputMode="numeric" value={f.bank_transit} onChange={(e) => set("bank_transit", e.target.value)} placeholder="30001" data-testid="paie-portal-emp-bank-transit" /></Field>
            <Field label={initial?.bank_account_masked ? `N° de compte (actuel ${initial.bank_account_masked})` : "N° de compte"}>
              <input className={inputCls} inputMode="numeric" value={f.bank_account} onChange={(e) => set("bank_account", e.target.value)} placeholder={initial?.bank_account_masked ? "Laisser vide pour conserver" : ""} data-testid="paie-portal-emp-bank-account" />
            </Field>
          </>
        )}
      </Group>

      {summary.ccq_enabled && (
        <Group title="CCQ — industrie de la construction">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 sm:col-span-2 lg:col-span-3 cursor-pointer">
            <input type="checkbox" checked={!!f.ccq.active} onChange={(e) => setC("active", e.target.checked)} className="w-4 h-4" data-testid="paie-portal-emp-ccq-active" /> Employé assujetti à la CCQ
          </label>
          {f.ccq.active && (
            <>
              <Field label="Métier *">
                <select className={inputCls} value={f.ccq.trade_id || ""} onChange={(e) => { setC("trade_id", e.target.value); setC("level_code", ""); }} data-testid="paie-portal-emp-ccq-trade">
                  <option value="">— choisir —</option>{(summary.ccq_trades || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {!(summary.ccq_trades || []).length && <p className="text-[10px] text-amber-700 mt-0.5">Aucun métier configuré par votre gestionnaire.</p>}
              </Field>
              <Field label="Niveau (apprenti / compagnon)">
                <select className={inputCls} value={f.ccq.level_code || ""} onChange={(e) => setC("level_code", e.target.value)} data-testid="paie-portal-emp-ccq-level">
                  <option value="">— choisir —</option>{(trade?.levels || []).map((lv) => <option key={lv.code} value={lv.code}>{lv.label}</option>)}
                </select>
              </Field>
              <Field label="N° CCQ du salarié"><input className={inputCls} value={f.ccq.ccq_no || ""} onChange={(e) => setC("ccq_no", e.target.value)} data-testid="paie-portal-emp-ccq-no" /></Field>
              <Field label="Syndicat / local"><input className={inputCls} value={f.ccq.union_name || ""} onChange={(e) => setC("union_name", e.target.value)} /></Field>
            </>
          )}
        </Group>
      )}

      <Field label="Notes pour votre gestionnaire"><textarea className={`${inputCls} min-h-[70px]`} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Ex. : déductions particulières, horaire spécial, avance consentie…" data-testid="paie-portal-emp-notes" /></Field>

      <div className={formActionsCls}>
        <button type="button" onClick={onCancel} className={cancelBtnCls} data-testid="paie-portal-emp-cancel">Annuler</button>
        <button type="button" onClick={submit} disabled={saving} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="paie-portal-emp-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} {initial ? "Enregistrer les modifications" : "Ajouter l'employé"}
        </button>
      </div>
    </div>
  );
}
